import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { useTranslation } from 'react-i18next';

// Worker pdf.js servi depuis public/ (copié de pdfjs-dist/legacy/build).
// Bundlé avec l'app → fonctionne hors-ligne + dans la webview Capacitor
// (contrairement à window.open(blob/dataURL) qui échoue en webview mobile).
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

function base64EnOctets(base64) {
  const raw = base64.includes(',') ? base64.split(',')[1] : base64;
  const bin = atob(raw);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function echapperHtml(texte) {
  return String(texte).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

/**
 * Imprime un PDF directement (boîte d'impression du navigateur), sans ouvrir
 * d'onglet. Chaque page est rendue par pdf.js en image haute résolution dans
 * un iframe caché, puis on appelle print() sur cet iframe. Fonctionne aussi
 * dans Safari, qui n'imprime pas un PDF chargé dans un iframe.
 */
export async function imprimerPdf(bytes, filename, pdfjs = pdfjsLib) {
  const ECHELLE = 3; // 72 pt × 3 ≈ 216 ppp : texte net à l'impression
  const pdf = await pdfjs.getDocument({ data: bytes }).promise;
  const urls = [];
  let largeurPt = 612;
  let hauteurPt = 792;
  try {
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n);
      const base = page.getViewport({ scale: 1 });
      if (n === 1) { largeurPt = base.width; hauteurPt = base.height; }
      const viewport = page.getViewport({ scale: ECHELLE });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      const image = await new Promise((ok, ko) => canvas.toBlob(
        (b) => (b ? ok(b) : ko(new Error('toBlob'))), 'image/png',
      ));
      canvas.width = 0; canvas.height = 0; // libère la mémoire du canvas
      urls.push(URL.createObjectURL(image));
    }
  } catch (e) {
    urls.forEach((u) => URL.revokeObjectURL(u));
    throw e;
  } finally {
    try { pdf.destroy(); } catch (_) {}
  }

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${echapperHtml(filename)}</title><style>
@page { size: ${largeurPt}pt ${hauteurPt}pt; margin: 0; }
html, body { margin: 0; padding: 0; background: #fff; }
.page { break-after: page; page-break-after: always; }
.page:last-child { break-after: auto; page-break-after: auto; }
img { display: block; width: 100%; height: auto; margin: 0 auto; }
</style></head><body>${urls.map((u) => `<div class="page"><img src="${u}" alt=""></div>`).join('')}</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  // Hors écran mais À LA TAILLE DE LA PAGE : Safari imprime avec la fenêtre de
  // l'iframe (un iframe de 1 px donnait un aperçu d'impression vide).
  iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${largeurPt}pt;height:${hauteurPt}pt;border:0;`;
  document.body.appendChild(iframe);
  let nettoye = false;
  const nettoyer = () => {
    if (nettoye) return;
    nettoye = true;
    iframe.remove();
    urls.forEach((u) => URL.revokeObjectURL(u));
  };
  try {
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    await Promise.all(Array.from(doc.images).map((img) => (
      img.decode ? img.decode().catch(() => {}) : Promise.resolve()
    )));
    const fen = iframe.contentWindow;
    fen.addEventListener('afterprint', () => setTimeout(nettoyer, 0));
    setTimeout(nettoyer, 10 * 60 * 1000); // filet si afterprint n'arrive jamais
    fen.focus();
    fen.print();
  } catch (e) {
    nettoyer();
    throw e;
  }
}

/**
 * Visionneuse PDF in-app (rend chaque page dans un <canvas>).
 * Mobile-safe : ne dépend pas de window.open / <a download> (cassés en webview
 * Capacitor). Affiche le PDF directement dans une modale plein écran.
 *
 * Props : base64 (string, avec ou sans préfixe data:), filename, onClose.
 */
const PdfViewerModal = ({ base64, filename = 'document.pdf', onClose }) => {
  const { t } = useTranslation();
  const conteneurRef = useRef(null);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [impressionEnCours, setImpressionEnCours] = useState(false);

  useEffect(() => {
    let annule = false;
    let pdf = null;
    (async () => {
      try {
        const bytes = base64EnOctets(base64);
        pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (annule) return;
        const conteneur = conteneurRef.current;
        if (!conteneur) return;
        conteneur.innerHTML = '';
        const largeurDispo = Math.min(conteneur.clientWidth || 800, 900);
        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n);
          if (annule) return;
          const base = page.getViewport({ scale: 1 });
          const scale = Math.max(0.2, largeurDispo / base.width);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto 12px';
          canvas.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
          conteneur.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        }
        if (!annule) setChargement(false);
      } catch (e) {
        if (!annule) { setErreur(e?.message || t('pdfViewer.erreurDeLectureDuPdf')); setChargement(false); }
      }
    })();
    return () => { annule = true; if (pdf) { try { pdf.destroy(); } catch (_) {} } };
  }, [base64]);

  // Deux boutons distincts (demande du propriétaire) :
  //
  //  • IMPRIMER : ouvre DIRECTEMENT la boîte d'impression (imprimerPdf), sur
  //    ordinateur comme dans Safari iOS / Chrome Android. Exception : l'APP
  //    native (Capacitor) — la webview iOS/Android ne gère pas window.print() —
  //    passe par la feuille de partage native (« Imprimer » / AirPrint sur iOS).
  //    Repli si l'impression directe échoue : le PDF dans un onglet.
  //
  //  • PARTAGER : feuille de partage native (Courriel, Messages, Fichiers, AirDrop…)
  //    via l'API Web Share ; navigateur sans partage de fichiers (ex. Firefox
  //    ordinateur) : téléchargement du PDF.
  const estAppNative = () => {
    try {
      return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    } catch (_) { return false; }
  };

  // Retourne true si la feuille de partage a été ouverte (ou annulée par l'utilisateur).
  const ouvrirFeuilleDePartage = async (blob) => {
    try {
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (!(navigator.canShare && navigator.canShare({ files: [file] }))) return false;
      await navigator.share({ files: [file], title: filename });
      return true;
    } catch (_) {
      return true; // annulé par l'utilisateur
    }
  };

  const ouvrirDansOnglet = (blob) => {
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (!w) {
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const imprimer = async () => {
    const blob = new Blob([base64EnOctets(base64)], { type: 'application/pdf' });
    if (estAppNative()) {
      if (await ouvrirFeuilleDePartage(blob)) return;
      ouvrirDansOnglet(blob);
      return;
    }
    if (impressionEnCours) return;
    setImpressionEnCours(true);
    try {
      await imprimerPdf(base64EnOctets(base64), filename);
    } catch (_) {
      ouvrirDansOnglet(blob); // impression directe impossible
    } finally {
      setImpressionEnCours(false);
    }
  };

  const partager = async () => {
    const blob = new Blob([base64EnOctets(base64)], { type: 'application/pdf' });
    if (await ouvrirFeuilleDePartage(blob)) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000010,
    display: 'flex', flexDirection: 'column',
  };
  const header = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.6rem 1rem', background: '#1f2937', color: 'white', flex: '0 0 auto',
  };
  const body = { flex: '1 1 auto', overflowY: 'auto', padding: '12px', background: '#374151' };
  const btn = { background: 'none', border: '1px solid rgba(255,255,255,0.5)', color: 'white', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.85rem' };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={header}>
        <span style={{ fontSize: '0.9rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {filename}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={impressionEnCours ? { ...btn, opacity: 0.6, cursor: 'wait' } : btn} onClick={imprimer} disabled={impressionEnCours}>{t('pdfViewer.imprimer')}</button>
          <button style={btn} onClick={partager}>{t('pdfViewer.partager')}</button>
          <button style={btn} onClick={onClose}>{t('pdfViewer.fermer')}</button>
        </div>
      </div>
      <div style={body}>
        {chargement && <p style={{ color: 'white', textAlign: 'center' }}>{t('pdfViewer.chargementDuPdf')}</p>}
        {erreur && <p style={{ color: '#fca5a5', textAlign: 'center' }}>{t('pdfViewer.impossibleDAfficherLePdfErreur', { erreur: erreur })}</p>}
        <div ref={conteneurRef} style={{ maxWidth: 900, margin: '0 auto' }} />
      </div>
    </div>
  );
};

export default PdfViewerModal;
